import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";
import { getCached, getStale, setCached, STATIC_TTL_MS } from "../utils/queryCache";
import { SEASON_YEAR } from "../utils/season";

// Best and worst manager week across every real league for one finished week,
// with the lineup behind each, via get_weekly_awards() (migration 065).
//
// Only ever asked for a week that is over, so the answer cannot change and is
// persisted for a day: one call per device per day at most, and a cold launch
// paints from disk. An empty answer is not cached, since it can also mean
// scoring had not run yet when the call was made.
//
// Returns { best, worst, loading }. best and worst are null when there is
// nothing to show.
export function useWeeklyAwards(week) {
  const key = `awards:v1:${SEASON_YEAR}:w${week}`;
  const initial = week ? getCached(key) || getStale(key) : null;
  const [rows, setRows] = useState(initial);
  const [loading, setLoading] = useState(Boolean(week) && !initial);

  useEffect(() => {
    if (!week) {
      setRows(null);
      setLoading(false);
      return undefined;
    }
    const cached = getCached(key);
    if (cached) {
      setRows(cached);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_weekly_awards", { p_week: week });
      if (cancelled) return;
      if (!error && data?.length) {
        setCached(key, data, STATIC_TTL_MS, { persist: true });
        setRows(data);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [key, week]);

  return {
    best: rows?.find((r) => r.kind === "best") || null,
    worst: rows?.find((r) => r.kind === "worst") || null,
    loading,
  };
}
